import { h, clear } from './dom';
import { tr, lang } from '@/i18n';
import { fmtClock } from '@/core/time';
import { LIMITS, validateFeedback, feedbackId, type FeedbackError, type FeedbackKind, type FeedbackPayload, type Idea, type IdeaStep } from '@/feedback/shared';
import type { Game } from '@/core/game';

/**
 * v0.9 玩家回饋表單：🐞 異常（說明＋圖片必填、Email 選填）／💡 需求（用遊戲任務的故事寫法）
 * 送到 /api/feedback（Cloudflare Pages Function → R2）。連不到時（離線版、還沒設定好）可以下載回饋檔。
 */
declare const __APP_VERSION__: string; declare const __COMMIT__: string;

export interface FeedbackDeps { g: Game; open(id: string, title: string, body: Node): void; close(): void; toast(msg: string): void }

const LIVE = 'https://food-factory.duckyit.com';
const endpoint = () => (location.protocol === 'file:' ? LIVE : '') + '/api/feedback';

interface Draft { text: string; email: string; images: string[]; context: boolean; idea: Idea }
const emptyIdea = (): Idea => ({ title: '', type: 'side', role: 'any', floor: '1F', steps: [{ floor: '1F', where: '', action: '', say: '' }], choice: { situation: '', good: '', bad: '', result: '' }, lesson: '' });
const drafts: Record<FeedbackKind, Draft> = { bug: load('bug'), idea: load('idea') };
function load(kind: FeedbackKind): Draft {
  const d: Draft = { text: '', email: '', images: [], context: true, idea: emptyIdea() };
  try { const s = localStorage.getItem(`ffg.feedback.${kind}`); if (s) Object.assign(d, JSON.parse(s), { images: [] }); } catch { /* 無痕模式 */ }
  return d;
}
function save(kind: FeedbackKind): void {
  try { const { images: _i, ...rest } = drafts[kind]; void _i; localStorage.setItem(`ffg.feedback.${kind}`, JSON.stringify(rest)); } catch { /* ignore */ }
}

const ERR = (): Record<FeedbackError, string> => ({
  kind: tr("回饋類型不對"), text: tr("說明要寫 5～4000 個字"), image: tr("請至少附一張圖片（截圖、拍照都可以）"), imageType: tr("圖片格式不支援（請用 JPG、PNG）"),
  imageSize: tr("圖片太大了"), tooMany: tr("圖片最多 3 張、步驟最多 12 步"), email: tr("Email 格式不對（不想留可以空白）"), title: tr("請幫想法取一個標題（2～80 字）"),
  tooBig: tr("內容太大了，請少放幾張圖"), spam: tr("送出失敗"),
});

/** 圖片壓縮：最長邊 1600 px、JPEG；太大再縮 */
async function compress(src: Blob | string): Promise<string> {
  const url = typeof src === 'string' ? src : URL.createObjectURL(src);
  try {
    const img = await new Promise<HTMLImageElement>((ok, no) => { const i = new Image(); i.onload = () => ok(i); i.onerror = no; i.src = url; });
    for (const [max, q] of [[1600, 0.82], [1200, 0.72], [900, 0.6]] as const) {
      const s = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
      const cv = document.createElement('canvas'); cv.width = Math.round(img.naturalWidth * s); cv.height = Math.round(img.naturalHeight * s);
      cv.getContext('2d')!.drawImage(img, 0, 0, cv.width, cv.height);
      const out = cv.toDataURL('image/jpeg', q);
      if (out.length < LIMITS.imageChars * 0.9) return out;
    }
    throw new Error('too big');
  } finally { if (typeof src !== 'string') URL.revokeObjectURL(url); }
}

/** 遊戲狀態（附在回饋裡，方便找問題） */
function context(g: Game): Record<string, unknown> {
  try { return contextInner(g); } catch { return { version: __APP_VERSION__, lang }; }
}
function contextInner(g: Game): Record<string, unknown> {
  const d = g.day; const role = d ? g.c.roleById.get(d.roleId) : null;
  return {
    version: __APP_VERSION__, contentVersion: g.c.game.contentVersion, commit: __COMMIT__, lang,
    screen: `${innerWidth}×${innerHeight}`,
    ...(d ? { day: d.day, role: role ? `${role.id}（${role.name}）` : d.roleId, floor: d.floor, zone: g.rt()?.zoneAt(...g.playerPos())?.name ?? '', pos: g.playerPos().map(n => Math.round(n * 10) / 10).join(','), clock: fmtClock(d.clock),
      log: d.log.slice(-15).map(l => `${fmtClock(l.at)} ${l.text}`) } : {}),
  };
}

/** 現在的遊戲畫面（3D 場景）截圖 */
function screenshot(g: Game): string | null {
  const st = g.stage; const r = st.renderer; if (!r) return null;
  try { r.render(st.scene, st.camera); return r.domElement.toDataURL('image/jpeg', 0.85); } catch { return null; }
}

export function openFeedback(deps: FeedbackDeps, kind: FeedbackKind): void {
  const { g } = deps; const d = drafts[kind];
  const body = h('form', { class: 'fb-form', novalidate: true });
  const errBox = h('div', { class: 'fb-errors', role: 'alert' });
  const field = (label: string, input: HTMLElement, note?: string, required = false) =>
    h('label', { class: 'fb-field' }, h('span', { class: 'fb-label' }, label, required ? h('em', {}, tr(" ＊必填")) : h('small', {}, tr("（選填）"))), input, note ? h('small', { class: 'fb-note' }, note) : null);
  const text = (v: string, set: (s: string) => void, attrs: Record<string, unknown> = {}, area = false) => {
    const el = h(area ? 'textarea' : 'input', { ...(area ? { rows: 5 } : { type: 'text' }), ...attrs }) as HTMLInputElement | HTMLTextAreaElement;
    el.value = v; el.oninput = () => { set(el.value); save(kind); }; return el;
  };
  const select = (v: string, opts: [string, string][], set: (s: string) => void) => {
    const el = h('select', {}, ...opts.map(([val, lab]) => h('option', { value: val, ...(val === v ? { selected: true } : {}) }, lab)));
    el.onchange = () => { set(el.value); save(kind); }; return el;
  };
  const FLOORS: [string, string][] = [['any', tr("不限")], ...g.c.floors.map(f => [f.id, f.id] as [string, string])];

  // ---------------------------------------------------------------- 圖片
  const thumbs = h('div', { class: 'fb-thumbs' });
  const drawThumbs = () => {
    clear(thumbs);
    d.images.forEach((src, i) => { const x = h('button', { type: 'button', class: 'fb-x', 'aria-label': tr("拿掉這張圖") }, '✕'); x.onclick = () => { d.images.splice(i, 1); drawThumbs(); };
      thumbs.append(h('div', { class: 'fb-thumb' }, h('img', { src, alt: tr("圖片 {0}", i + 1) }), x)); });
    if (!d.images.length) thumbs.append(h('small', { class: 'fb-note' }, kind === 'bug' ? tr("還沒有圖片。可以用下面的按鈕，或直接 Ctrl／⌘＋V 貼上截圖。") : tr("可以附圖（手繪、截圖、照片都可以）。")));
  };
  const addImage = async (src: Blob | string) => {
    if (d.images.length >= LIMITS.images) { deps.toast(tr("圖片最多 3 張。")); return; }
    try { d.images.push(await compress(src)); drawThumbs(); } catch { deps.toast(tr("這張圖讀不出來，換一張試試。")); }
  };
  const file = h('input', { type: 'file', accept: 'image/*', multiple: true, class: 'fb-file' }) as HTMLInputElement;
  file.onchange = async () => { for (const f of [...(file.files ?? [])]) await addImage(f); file.value = ''; };
  const pick = h('button', { type: 'button', class: 'btn secondary small' }, tr("🖼️ 選擇圖片")); pick.onclick = () => file.click();
  const shot = h('button', { type: 'button', class: 'btn secondary small' }, tr("📸 用現在的遊戲畫面"));
  shot.onclick = () => { const s = screenshot(g); if (s) void addImage(s); else deps.toast(tr("截不到畫面，請用選擇圖片。")); };
  body.addEventListener('paste', e => { for (const it of [...(e.clipboardData?.items ?? [])]) if (it.type.startsWith('image/')) { const f = it.getAsFile(); if (f) void addImage(f); e.preventDefault(); } });
  const imageBlock = h('div', { class: 'fb-field' }, h('span', { class: 'fb-label' }, tr("圖片"), kind === 'bug' ? h('em', {}, tr(" ＊必填（最多 3 張）")) : h('small', {}, tr("（選填，最多 3 張）"))),
    thumbs, h('div', { class: 'row' }, shot, pick), file);

  // ---------------------------------------------------------------- 共同欄位
  const email = text(d.email, v => (d.email = v), { type: 'email', inputmode: 'email', autocomplete: 'email', placeholder: 'name@example.com' });
  const ctxBox = h('input', { type: 'checkbox', ...(d.context ? { checked: true } : {}) }) as HTMLInputElement; ctxBox.onchange = () => { d.context = ctxBox.checked; save(kind); };
  const honey = h('input', { type: 'text', name: 'website', tabindex: '-1', autocomplete: 'off', class: 'fb-honey', 'aria-hidden': 'true' }) as HTMLInputElement;

  if (kind === 'bug') {
    body.append(
      h('p', { class: 'small' }, tr("遇到怪怪的地方（卡住、穿牆、字寫錯、按了沒反應、當掉）？寫下來、附上截圖，會統一收集起來，用來改進遊戲。")),
      field(tr("說明"), text(d.text, v => (d.text = v), { placeholder: tr("例：在 2F 調理區推烏龜車進冷卻室，卡在門口出不來；按 E 也沒反應。"), maxlength: LIMITS.text }, true), tr("在哪裡、做了什麼、發生什麼事、你覺得應該怎樣。"), true),
      imageBlock);
  } else {
    const I = d.idea;
    const steps = h('div', { class: 'fb-steps' });
    const drawSteps = () => {
      clear(steps);
      I.steps.forEach((s, i) => {
        const rm = h('button', { type: 'button', class: 'fb-x', 'aria-label': tr("刪掉這一步") }, '✕'); rm.onclick = () => { I.steps.splice(i, 1); save(kind); drawSteps(); };
        const set = (k: keyof IdeaStep) => (v: string) => { s[k] = v; };
        steps.append(h('div', { class: 'fb-step' }, h('b', {}, tr("第 {0} 步", i + 1)), rm,
          select(s.floor, FLOORS, set('floor')),
          text(s.where, set('where'), { placeholder: tr("去哪裡／找誰（例：1F 碼頭拉門、倉儲課長阿忠）"), maxlength: LIMITS.stepText }),
          text(s.action, set('action'), { placeholder: tr("要做什麼（例：先鋪防滑墊再卸貨）"), maxlength: LIMITS.stepText }),
          text(s.say, set('say'), { placeholder: tr("角色說的話（例：「地上好滑，先鋪墊子！」）"), maxlength: LIMITS.stepText })));
      });
      const add = h('button', { type: 'button', class: 'btn secondary small' }, tr("＋ 加一步")); add.disabled = I.steps.length >= LIMITS.steps;
      add.onclick = () => { I.steps.push({ floor: I.floor === 'any' ? '1F' : I.floor, where: '', action: '', say: '' }); save(kind); drawSteps(); };
      steps.append(add);
    };
    drawSteps();
    const C = I.choice ??= { situation: '', good: '', bad: '', result: '' };
    const TYPES: [Idea['type'], string][] = [['side', tr("支線任務")], ['main', tr("主線任務（某個職務每天要做的）")], ['daily', tr("每日任務")], ['accident', tr("突發事件／事故")], ['case', tr("異常案件")], ['system', tr("新玩法／系統")], ['other', tr("其他")]];
    const preview = h('div', { class: 'fb-preview' });
    const showPreview = () => {
      clear(preview);
      preview.append(h('div', { class: 'section-label' }, tr("預覽（遊戲裡大概會長這樣）")),
        h('div', { class: 'card fb-card' }, h('b', {}, `💡 ${I.title || tr("（還沒取標題）")}`), h('small', {}, `${TYPES.find(t => t[0] === I.type)?.[1] ?? ''}・${I.role === 'any' ? tr("不限職務") : g.c.roleById.get(I.role)?.name ?? ''}・${I.floor === 'any' ? tr("不限樓層") : I.floor}`),
          h('p', {}, d.text || tr("（故事背景）")),
          h('ol', {}, ...I.steps.filter(s => s.where || s.action || s.say).map(s => h('li', {}, `【${s.floor}・${s.where || '?'}】${s.action}`, s.say ? h('div', { class: 'small' }, `💬 ${s.say}`) : null))),
          C.situation ? h('p', { class: 'small' }, tr("❓ {0}　✅ {1}　❌ {2}", C.situation, C.good, C.bad)) : null,
          I.lesson ? h('p', { class: 'small' }, tr("📖 學到：{0}", I.lesson)) : null));
    };
    const pv = h('button', { type: 'button', class: 'btn secondary small' }, tr("👀 預覽")); pv.onclick = showPreview;
    body.append(
      h('p', { class: 'small' }, tr("有好玩的點子？用遊戲裡任務的寫法寫下來：發生在哪裡、要做什麼、誰說了什麼、有什麼選擇。開發者看過、覺得可以，就會做進遊戲！")),
      field(tr("標題"), text(I.title, v => (I.title = v), { placeholder: tr("例：下雨天的碼頭"), maxlength: LIMITS.title }), undefined, true),
      h('div', { class: 'fb-grid' },
        field(tr("類型"), select(I.type, TYPES, v => (I.type = v as Idea['type']))),
        field(tr("哪個職務會遇到"), select(I.role, [['any', tr("不限")], ...g.c.roles.map(r => [r.id, `${r.deptName}・${r.name}`] as [string, string])], v => (I.role = v))),
        field(tr("主要在哪一層"), select(I.floor, FLOORS, v => (I.floor = v)))),
      field(tr("故事背景"), text(d.text, v => (d.text = v), { placeholder: tr("例：今天下大雨，1F 碼頭積水。冷凍車到了，司機一直催。倉儲要先處理積水、鋪防滑墊，才能安全卸貨。"), maxlength: LIMITS.text }, true), undefined, true),
      h('div', { class: 'fb-field' }, h('span', { class: 'fb-label' }, tr("步驟"), h('small', {}, tr("（選填：一步一件事，像遊戲裡的任務）"))), steps),
      h('details', { class: 'fb-choice', ...(C.situation ? { open: true } : {}) }, h('summary', {}, tr("❓ 加一個選擇題（選填）")),
        field(tr("情境"), text(C.situation, v => (C.situation = v), { placeholder: tr("例：司機催你快點卸貨") })),
        field(tr("✅ 好的做法"), text(C.good, v => (C.good = v), { placeholder: tr("例：先鋪好防滑墊再卸") })),
        field(tr("❌ 不好的做法"), text(C.bad, v => (C.bad = v), { placeholder: tr("例：直接卸，反正很快") })),
        field(tr("選了好的做法會怎樣"), text(C.result, v => (C.result = v), { placeholder: tr("例：沒有人滑倒，貨也準時進冷凍庫") }))),
      field(tr("玩家會學到"), text(I.lesson ?? '', v => (I.lesson = v), { placeholder: tr("例：下雨天要先處理積水，防止滑倒") })),
      imageBlock, h('div', { class: 'row' }, pv), preview);
  }
  body.append(
    field(tr("Email"), email, tr("想知道處理結果再留；只用來回覆這則回饋。")),
    h('label', { class: 'check' }, ctxBox, tr(" 附上遊戲狀態（版本、樓層、職務、時間、最近的紀錄），比較好找問題")),
    honey, errBox);
  drawThumbs();

  // ---------------------------------------------------------------- 送出
  const submit = h('button', { type: 'submit', class: 'btn primary' }, kind === 'bug' ? tr("🐞 送出異常回報") : tr("💡 送出想法"));
  body.append(h('div', { class: 'row fb-actions' }, submit));
  const payload = (): FeedbackPayload => ({ kind, text: d.text.trim(), email: d.email.trim() || undefined, images: d.images,
    ...(kind === 'idea' ? { idea: { ...d.idea, title: d.idea.title.trim() } } : {}), context: d.context ? context(g) : { version: __APP_VERSION__, lang }, website: honey.value });
  const showErrors = (errs: FeedbackError[]) => { clear(errBox); if (errs.length) errBox.append(h('ul', {}, ...[...new Set(errs)].map(e => h('li', {}, ERR()[e])))); };
  const offline = (p: FeedbackPayload, why: string) => {
    clear(errBox);
    const dl = h('button', { type: 'button', class: 'btn secondary' }, tr("💾 下載回饋檔"));
    dl.onclick = () => { const id = feedbackId(); const a = h('a', { href: URL.createObjectURL(new Blob([JSON.stringify({ ...p, id }, null, 1)], { type: 'application/json' })), download: `food-factory-${kind}-${id}.json` }); a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000); };
    errBox.append(h('p', {}, tr("⚠️ 現在連不到回饋伺服器（{0}）。可以先下載回饋檔，傳給開發者；或等一下再送一次。", why)), dl);
  };
  body.onsubmit = async e => {
    e.preventDefault();
    const p = payload();
    const errs = validateFeedback(p).filter(x => x !== 'spam');
    showErrors(errs); if (errs.length) return;
    submit.disabled = true; submit.textContent = tr("送出中…");
    try {
      const r = await fetch(endpoint(), { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(p) });
      const j = await r.json().catch(() => ({})) as { ok?: boolean; id?: string; errors?: FeedbackError[] };
      if (r.ok && j.ok) {
        drafts[kind] = { text: '', email: d.email, images: [], context: d.context, idea: emptyIdea() }; save(kind);
        clear(body);
        body.append(h('div', { class: 'fb-done' }, h('p', { class: 'big' }, kind === 'bug' ? tr("🙏 收到了，謝謝回報！") : tr("🙌 收到你的想法了！")),
          h('p', {}, tr("回饋編號：{0}", j.id ?? '')), h('p', { class: 'small' }, kind === 'bug' ? tr("問題會統一收集，修好後會寫在更新紀錄。") : tr("開發者會評估，覺得可以就會做進遊戲。"))));
        const ok = h('button', { type: 'button', class: 'btn primary' }, tr("好")); ok.onclick = () => deps.close(); body.append(ok);
        return;
      }
      if (j.errors?.length) showErrors(j.errors); else offline(p, `HTTP ${r.status}`);
    } catch { offline(p, tr("離線或網路問題")); }
    finally { if (submit.isConnected) { submit.disabled = false; submit.textContent = kind === 'bug' ? tr("🐞 送出異常回報") : tr("💡 送出想法"); } }
  };
  deps.open(`feedback-${kind}`, kind === 'bug' ? tr("🐞 回報問題（異常）") : tr("💡 我有好玩的想法（需求）"), body);
}
